import { t } from "@/lib/i18n/interpolate";
import { exponentForm } from "@/lib/tool-kit/math/primes";
import type { NumberProperties } from "@/lib/tool-kit/number-theory/properties";

/** The strings the property table needs; the widget's string table satisfies this. */
export interface PropertyStrings {
  yes: string;
  no: string;
  even: string;
  odd: string;
  prime: string;
  composite: string;
  neither: string;
  propParity: string;
  propPrimality: string;
  propSquare: string;
  propCube: string;
  propPowerOfTwo: string;
  propTriangular: string;
  propFibonacci: string;
  propPalindrome: string;
  propBinaryPalindrome: string;
  propSquarefree: string;
  detailEven: string;
  detailOdd: string;
  detailPrime: string;
  detailNeither: string;
  detailSquareYes: string;
  detailSquareNo: string;
  detailCubeYes: string;
  detailCubeNo: string;
  detailPowerYes: string;
  detailPowerNo: string;
  detailTriangularYes: string;
  detailTriangularNo: string;
  detailFibonacciYes: string;
  detailFibonacciOne: string;
  detailFibonacciNo: string;
  detailPalindromeYes: string;
  detailPalindromeNo: string;
  detailBinary: string;
  detailSquarefreeYes: string;
  detailSquarefreeNo: string;
}

export type PropertyRow = [string, string, string];

const ONE = BigInt(1);
const TWO = BigInt(2);

function yesNo(value: boolean, strings: PropertyStrings): string {
  return value ? strings.yes : strings.no;
}

function primality(p: NumberProperties, s: PropertyStrings): PropertyRow {
  const n = String(p.n);
  if (p.n === ONE) {
    return [s.propPrimality, s.neither, s.detailNeither];
  }
  const form = exponentForm(p.factorization.factors);
  return p.isPrime
    ? [s.propPrimality, s.prime, t(s.detailPrime, { n })]
    : [s.propPrimality, s.composite, `${n} = ${form}`];
}

function squareRow(p: NumberProperties, s: PropertyStrings): PropertyRow {
  const { is, floorRoot } = p.square;
  const detail = is
    ? t(s.detailSquareYes, { n: String(p.n), r: String(floorRoot) })
    : t(s.detailSquareNo, {
        a: String(floorRoot),
        sa: String(floorRoot * floorRoot),
        b: String(floorRoot + ONE),
        sb: String((floorRoot + ONE) * (floorRoot + ONE)),
      });
  return [s.propSquare, yesNo(is, s), detail];
}

function cubeRow(p: NumberProperties, s: PropertyStrings): PropertyRow {
  const { is, floorRoot } = p.cube;
  const next = floorRoot + ONE;
  const detail = is
    ? t(s.detailCubeYes, { n: String(p.n), r: String(floorRoot) })
    : t(s.detailCubeNo, {
        a: String(floorRoot),
        sa: String(floorRoot ** BigInt(3)),
        b: String(next),
        sb: String(next ** BigInt(3)),
      });
  return [s.propCube, yesNo(is, s), detail];
}

function triangularRow(p: NumberProperties, s: PropertyStrings): PropertyRow {
  const { is, index } = p.triangular;
  const at = (k: bigint) => String((k * (k + ONE)) / TWO);
  const detail = is
    ? t(s.detailTriangularYes, { n: String(p.n), k: String(index) })
    : t(s.detailTriangularNo, {
        k: String(index),
        a: at(index),
        k1: String(index + ONE),
        b: at(index + ONE),
      });
  return [s.propTriangular, yesNo(is, s), detail];
}

function fibonacciRow(p: NumberProperties, s: PropertyStrings): PropertyRow {
  const { is, indices, previous, next } = p.fibonacci;
  let detail: string;
  if (!is) {
    detail = t(s.detailFibonacciNo, {
      a: String(previous),
      b: String(next),
    });
  } else if (indices.length > 1) {
    detail = s.detailFibonacciOne;
  } else {
    detail = t(s.detailFibonacciYes, {
      k: String(indices[0]),
      n: String(p.n),
    });
  }
  return [s.propFibonacci, yesNo(is, s), detail];
}

/** The yes/no property table, one row per classification. */
export function propertyRows(
  p: NumberProperties,
  s: PropertyStrings
): PropertyRow[] {
  const n = String(p.n);
  const half = String(p.n / TWO);
  const power = p.powerOfTwo;
  const rows: PropertyRow[] = [
    [
      s.propParity,
      p.even ? s.even : s.odd,
      t(p.even ? s.detailEven : s.detailOdd, { n, half }),
    ],
    primality(p, s),
    squareRow(p, s),
    cubeRow(p, s),
    [
      s.propPowerOfTwo,
      yesNo(power.is, s),
      power.is
        ? t(s.detailPowerYes, { n, e: String(power.exponent) })
        : t(s.detailPowerNo, { binary: p.binary }),
    ],
    triangularRow(p, s),
    fibonacciRow(p, s),
    [
      s.propPalindrome,
      yesNo(p.decimalPalindrome, s),
      p.decimalPalindrome
        ? s.detailPalindromeYes
        : t(s.detailPalindromeNo, { reversed: p.reversed }),
    ],
    [
      s.propBinaryPalindrome,
      yesNo(p.binaryPalindrome, s),
      t(s.detailBinary, { binary: p.binary }),
    ],
  ];
  if (p.squarefree !== null) {
    rows.push([
      s.propSquarefree,
      yesNo(p.squarefree, s),
      p.squarefree
        ? s.detailSquarefreeYes
        : t(s.detailSquarefreeNo, {
            factors: exponentForm(p.factorization.factors),
          }),
    ]);
  }
  return rows;
}
